import React from "react";
import Link from "next/link";
import Icon from "../components/Icon";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { sharedStyles, SectionHead } from "./_shared";

const styles = `
.t26-horaires__card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  padding: clamp(20px, 3vw, 32px);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(20px, 3vw, 36px);
  box-shadow: var(--shadow-sm);
}
@media (max-width: 720px) {
  .t26-horaires__card { grid-template-columns: 1fr; gap: 18px; }
}
.t26-horaires__col h4 {
  font-family: var(--font-body);
  font-size: 11px; font-weight: 700;
  letter-spacing: .22em; text-transform: uppercase;
  color: var(--secondary);
  margin: 0 0 14px;
}
.t26-horaires__row {
  display: flex; justify-content: space-between; align-items: baseline;
  padding: 10px 0;
  border-bottom: 1px dashed var(--line);
}
.t26-horaires__row:last-child { border-bottom: 0; }
.t26-horaires__row-label {
  font-size: 14px; color: var(--ink);
}
.t26-horaires__row-time {
  font-family: var(--font-display);
  font-size: 15px; font-weight: 700;
  color: var(--primary);
}
`;

/**
 * Affiche un aperçu simple des horaires de l'org.
 * Lecture directe de `org.horaires` (structure: [{name, heure, minute, fixed, zeman, dow:[0..6]}]).
 * Affiche les 2 prochains horaires du jour + lien "Voir tous les horaires".
 *
 * Note : pour le calcul des zmanim variables, on délègue à la page Horaires
 * (HebcalService). Ici on affiche uniquement les horaires fixes (les plus
 * simples et stables) en preview.
 */
export default function HorairesPreview({ context, limit = 6, eyebrow, title, subtitle }) {
    const { org, langValue } = context;
    if (!Array.isArray(org?.horaires) || org.horaires.length === 0) return null;

    const today = new Date().getDay(); // 0=dimanche
    const tomorrow = (today + 1) % 7;

    const filter = (dow) =>
        org.horaires
            .filter(h => h.fixed && Array.isArray(h.dow) && h.dow.includes(dow))
            .sort((a, b) => ((a.heure || 0) * 60 + (a.minute || 0)) - ((b.heure || 0) * 60 + (b.minute || 0)))
            .slice(0, limit);

    const todayList = filter(today);
    const tomorrowList = filter(tomorrow);
    if (todayList.length === 0 && tomorrowList.length === 0) return null;

    const fmt = (h) => {
        const d = new Date();
        d.setHours(h.heure ?? 0, h.minute ?? 0, 0, 0);
        return d.toLocaleTimeString(langValue || "fr", { hour: "2-digit", minute: "2-digit" });
    };

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: sharedStyles + styles }} />
            <section className="t26-section">
                <SectionHead
                    eyebrow={eyebrow || t`Horaires`}
                    title={title || t`Horaires de la semaine`}
                    subtitle={subtitle || t`Tous les horaires fixes de notre programme hebdomadaire`}
                />

                <div className="t26-horaires__card">
                    {todayList.length > 0 && (
                        <div className="t26-horaires__col">
                            <h4><Trans>Aujourd'hui</Trans></h4>
                            {todayList.map((h, i) => (
                                <div key={i} className="t26-horaires__row">
                                    <span className="t26-horaires__row-label">{h.name}</span>
                                    <span className="t26-horaires__row-time">{fmt(h)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {tomorrowList.length > 0 && (
                        <div className="t26-horaires__col">
                            <h4><Trans>Demain</Trans></h4>
                            {tomorrowList.map((h, i) => (
                                <div key={i} className="t26-horaires__row">
                                    <span className="t26-horaires__row-label">{h.name}</span>
                                    <span className="t26-horaires__row-time">{fmt(h)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div style={{ textAlign: "center" }}>
                    <Link href={`${org.BASE_WEB_URL}/horaires`} legacyBehavior>
                        <a className="t26-section__more">
                            <Trans>Tous les horaires</Trans>
                            <span className="t26-section__more-arrow">
                                <Icon name="arrowRight" size={14} className="t26-flip-rtl" />
                            </span>
                        </a>
                    </Link>
                </div>
            </section>
        </>
    );
}
